import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Brain,
  AlertTriangle,
  AlertOctagon,
  TrendingUp,
  Activity,
  Gauge,
  Calendar,
  Wrench,
} from 'lucide-react';

interface Prediction {
  id: string;
  vehicle_name: string;
  component: string;
  risk_score: number;
  risk_level: string;
  miles_until_failure: number;
  predicted_failure_date: string;
  recommendation: string;
  confidence: number;
}

interface FleetRisk {
  fleet_risk_score: number;
  vehicles_at_risk: number;
  total_predictions: number;
  by_risk_level: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  critical_predictions: Prediction[];
  top_risks: Prediction[];
}

const MOCK_RISK: FleetRisk = {
  fleet_risk_score: 42,
  vehicles_at_risk: 5,
  total_predictions: 28,
  by_risk_level: { critical: 2, high: 5, medium: 12, low: 9 },
  critical_predictions: [],
  top_risks: [],
};

const RISK_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  critical: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444', bar: '#EF4444' },
  high: { bg: 'rgba(249,115,22,0.15)', text: '#F97316', bar: '#F97316' },
  medium: { bg: 'rgba(234,179,8,0.15)', text: '#EAB308', bar: '#EAB308' },
  low: { bg: 'rgba(34,197,94,0.15)', text: '#22C55E', bar: '#22C55E' },
};

function riskScoreColor(score: number): string {
  if (score >= 80) return '#EF4444';
  if (score >= 60) return '#F97316';
  if (score >= 40) return '#EAB308';
  return '#22C55E';
}

export default function PredictivePage() {
  const { data, isLoading } = useApi<FleetRisk>('/api/predictions/fleet-risk');
  const d = data ?? MOCK_RISK;
  const scoreColor = riskScoreColor(d.fleet_risk_score);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Predictive Maintenance"
        description="AI-powered analysis"
        actions={isLoading ? <Spinner size={16} /> : undefined}
      />

      {/* Fleet Risk Score + Stat Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Large Risk Gauge */}
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <Brain className="w-6 h-6 mb-2" style={{ color: scoreColor }} />
            <div className="text-5xl font-bold" style={{ color: scoreColor }}>
              {d.fleet_risk_score}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Fleet Risk Score
            </div>
          </CardContent>
        </Card>

        <StatCard icon={AlertTriangle} label="Vehicles at Risk" value={d.vehicles_at_risk} accent="warning" />
        <StatCard icon={AlertOctagon} label="Critical" value={d.by_risk_level.critical} accent="destructive" />
        <StatCard icon={TrendingUp} label="High" value={d.by_risk_level.high} accent="warning" />
        <StatCard icon={Activity} label="Medium" value={d.by_risk_level.medium} />
      </div>

      {/* Critical Predictions */}
      {d.critical_predictions.length > 0 && (
        <div>
          <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
            Critical Predictions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.critical_predictions.map((p) => {
              const rc = RISK_COLORS[p.risk_level] ?? { bg: 'rgba(234,179,8,0.15)', text: '#EAB308', bar: '#EAB308' };
              return (
                <div
                  key={p.id}
                  className="rounded-xl p-4 space-y-3"
                  style={{
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    backdropFilter: 'blur(var(--glass-blur))',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{p.vehicle_name}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: rc.bg, color: rc.text }}>
                      {p.risk_level}
                    </span>
                  </div>
                  <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <Wrench className="w-3 h-3 inline mr-1" />
                    {p.component}
                  </div>
                  {/* Risk Score Bar */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: 'var(--muted-foreground)' }}>Risk Score</span>
                      <span style={{ color: rc.text }}>{p.risk_score}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--glass-border)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${p.risk_score}%`, background: rc.bar }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <span className="flex items-center gap-1">
                      <Gauge className="w-3 h-3" />
                      {p.miles_until_failure.toLocaleString()} mi
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.predicted_failure_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-xs p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.08)', color: '#10B981' }}>
                    {p.recommendation}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Risks Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Top Risks
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Vehicle', 'Component', 'Risk Level', 'Risk Score', 'Confidence', 'Recommendation'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.top_risks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No predictions available — AI analysis pending
                    </td>
                  </tr>
                ) : (
                  d.top_risks.map((r) => {
                    const rc = RISK_COLORS[r.risk_level] ?? { bg: 'rgba(234,179,8,0.15)', text: '#EAB308', bar: '#EAB308' };
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{r.vehicle_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{r.component}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: rc.bg, color: rc.text }}>
                            {r.risk_level}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-16 rounded-full overflow-hidden" style={{ background: 'var(--glass-border)' }}>
                              <div className="h-full rounded-full" style={{ width: `${r.risk_score}%`, background: rc.bar }} />
                            </div>
                            <span className="text-xs font-mono" style={{ color: rc.text }}>{r.risk_score}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{r.confidence}%</td>
                        <td className="px-4 py-3 text-xs max-w-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                          {r.recommendation}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
